import React, { useEffect, useState } from "react";
import defaultLogo from "../images/login_Logo.svg";
import { useTheme } from "../context/ThemeContext";
import { resolveLogoUrl, DEFAULT_THEME } from "../Hooks/useTenantTheme";
import useTenantFromSubdomain from "../Hooks/useTenantFromSubdomain";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as yup from "yup";
import Input from "./Input";
import userIcon from "../images/userIcon.svg";
import { BsEyeFill } from "react-icons/bs";
import { BsEyeSlashFill } from "react-icons/bs";
import passwordIcon from "../images/passwordIcon.svg";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import ButtonLoader from "../Components/ButtonLoader";
import TenantSelector from "./TenantSelector";
import { persistSessionTokens } from "../utils/session";
import { buildTenantUrl } from "../Hooks/useTenantFromSubdomain";

const { REACT_APP_BASE_URL } = process.env;

const validationSchema = yup.object().shape({
  username: yup.string().required("Username is Required"),
  password: yup.string().required("Password is Required"),
});

const Login = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [tenantSelection, setTenantSelection] = useState(null); // { availableTenants, tempToken }
  const navigate = useNavigate();
  const themeContext = useTheme();
  const logo = resolveLogoUrl(themeContext?.theme?.logoUrl) || defaultLogo;
  const appName = themeContext?.theme?.appName || DEFAULT_THEME.appName;
  const tenantSubdomain = useTenantFromSubdomain();

  useEffect(() => {
    // Pre-login theming: identify the tenant by URL subdomain (no JWT exists
    // yet). A bare/unmatched host falls back to the default theme, which is
    // already what themeContext starts with, so there's nothing to fetch.
    if (tenantSubdomain) {
      themeContext?.fetchThemeBySubdomain(tenantSubdomain);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantSubdomain]);
  // Redirects the whole browser to the target tenant's subdomain when it
  // differs from the current one, carrying the session in the URL fragment
  // (see CrossLoginBridge.js) instead of navigating within this origin —
  // tenant identity is subdomain-derived app-wide, so staying on the wrong
  // subdomain after picking a different tenant would leave theming/context
  // mismatched with the actual active tenant.
  const goToTenant = (data) => {
    const targetSubdomain = data.tenant?.subdomain;
    if (targetSubdomain && targetSubdomain !== tenantSubdomain) {
      const params = new URLSearchParams({
        token: data.token,
        refreshToken: data.refreshToken || "",
        email: data.email || "",
        roles: data.roles || "",
        userId: data.id || "",
        hubspotUserId: data.hubspot_user_id || "",
        activeTenantId: data.activeTenantId ?? "",
        isSuperAdmin: data.isSuperAdmin ? "true" : "false",
      });
      window.location.replace(`${buildTenantUrl(targetSubdomain, "/auth/cross-login")}#${params.toString()}`);
      return;
    }

    persistSessionTokens(data);
    themeContext?.fetchTheme();
    navigate("/dashboard");
  };

  useEffect(() => {
    if (localStorage.getItem("token")) {
      navigate("/dashboard");
    }
  }, []);

  const handleSubmit = async (values, { resetForm }) => {
    try {
      setIsLoading(true)
      let data = {
        username: values?.username || "",
        password: values?.password || "",
        tenantSubdomain: tenantSubdomain || undefined,
      };
      await axios
        .post(`${REACT_APP_BASE_URL}/auth/signin`, data, {
          headers: {
            "Content-Type": "application/json",
          },
        })
        .then((res) => {
          if (res?.data?.requiresTenantSelection) {
            setTenantSelection({
              availableTenants: res.data.availableTenants,
              tempToken: res.data.tempToken,
            });
            return;
          }
          if (res?.data?.token) {
            persistSessionTokens(res.data);
            themeContext?.fetchTheme();
            navigate("/dashboard");
          }
        })
        .catch((error) => {
          if (error.response) {
            toast.error(error?.response?.data?.message);
          }
          else{
            toast.error("Something went wrong! Please try again in few minutes.")
          }
        });
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false)
    }
  };

  return (
    <div className="bg-canvas-primary h-screen">
      <div className="mainContainer flex flex-col justify-center items-center w-full h-full py-12 lg:py-24">
        <div className="bg-canvas-white py-11 px-5 flex gap-2 flex-col items-center rounded-3xl w-[350px]">
          <div>
            <img src={logo} alt="logo" />
          </div>
          <h1 className="text-canvas-primary font-bold text-center">{appName.toUpperCase()}</h1>
          {tenantSelection ? (
            <TenantSelector
              availableTenants={tenantSelection.availableTenants}
              tempToken={tenantSelection.tempToken}
              onSelected={goToTenant}
            />
          ) : (
          <>
          <p className="text-[11.25px] text-canvas-secondery">
            Enter your email and password below
          </p>
          <Formik
            enableReinitialize
            validationSchema={validationSchema}
            validateOnChange={false}
            onSubmit={handleSubmit}
            initialValues={{
              username: "",
              password: "",
            }}
          >
            {({ handleSubmit, handleChange, handleBlur, values }) => (
              <Form
                className="flex flex-col gap-2 w-full"
                noValidate
                onSubmit={handleSubmit}
              >
                <div>
                  <Field
                    name="username"
                    type="text"
                    as={Input}
                    value={values?.username}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    icon={userIcon}
                    label="Username"
                  />
                  <div className="h-4 leading-4 overflow-hidden">
                    <ErrorMessage
                      name="username"
                      component="span"
                      className="text-red-500 text-xs pl-2"
                    />
                  </div>
                </div>
                <div>
                  <Field
                    name="password"
                    type="password"
                    as={Input}
                    value={values?.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    icon={passwordIcon}
                    label="Password"
                    BsEyeFill={<BsEyeFill />}
                    BsEyeSlashFill={<BsEyeSlashFill />}
                  />
                  <div className="h-4 leading-4 overflow-hidden">
                    <ErrorMessage
                      name="password"
                      component="div"
                      className="text-red-500 text-xs pl-2"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between pb-3">
                  <Link
                    to="/generate-password"
                    className="text-xs font-outfit text-canvas-secondery"
                  >
                    Generate password?
                  </Link>
                  <Link
                    to="/forget-password"
                    className="text-xs font-outfit text-canvas-secondery"
                  >
                    Forgot password?
                  </Link>
                </div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex items-center gap-3 justify-center text-center font-outfit text-[12px] uppercase font-bold bg-canvas-primary text-canvas-white py-3 px-5 rounded-[37px] disabled:cursor-not-allowed disabled:bg-canvas-primary/20 disabled:text-canvas-primary"
                >
                  {
                    isLoading && (
                      <ButtonLoader/>
                    )
                  }
                  Login
                </button>
              </Form>
            )}
          </Formik>
          {/* <div className="flex gap-5 items-center w-[70%]">
            <div className="w-full h-[1px] bg-canvas-secondery"></div>
            <p className="text-canvas-secondery font-semibold">OR</p>
            <div className="w-full h-px bg-canvas-secondery"></div>
          </div>
          <div className="w-full rounded-[37px] py-3 px-5 border border-canvas-secondery flex justify-center items-center font-outfit text-[11px] uppercase font-bold text-canvas-gray">
            Login with Organisation ID
          </div> */}
          </>
          )}
        </div>
        {/* <Link
          to="/register"
          className="text-canvas-white font-outfit mt-4 font-semibold text-[12px]"
        >
          Don't have an account?
        </Link> */}
      </div>
    </div>
  );
};

export default Login;
